import React, { Component } from 'react'
import { func } from 'prop-types'
import { translate } from 'react-i18next'
import { initialization, stores, view } from '../../stores'
import { SubHero } from '../../components/header/SubHero'
import { GardenerDetail } from '../../components/gardeners/GardenerDetail'
import { PagesPaths } from '../../constants/Pages'
import { LeadFunnels } from '../../constants/LeadFunnels'
import { removeCanonicalLink, updateCanonicalLink } from '../../utilities/window'
import { addNoIndexIfNotExists, removeNoIndexIfExists, injectJsonLd, removeJsonLd, injectOpenGraph, removeOpenGraph } from '../../utilities/window'
import { buildGardenerJsonLd, buildGardenerMetaTitle, buildGardenerMetaDescription, addRatingsToJsonLd, buildGardenerBreadcrumbJsonLd } from '../../utilities/gardener-seo'
import { sanitizeZipCodes } from '../../utilities/validators'
import { getReviews } from '../../api/gardeners'

/**
 * Warning: This page might be called without code nor any data in cache
 * This is why we initialize the store without value, then try to find a way to redirect breadcrumbs to a "correct" page
 * Make extra precaution when changing any logic in breadcrumbs
 */
export class GardenerPageComponent extends Component {
  constructor (props) {
    super(props)
    this.state = { notFound: false, loading: true }
  }

  async componentDidMount () {
    const { match, t, history } = this.props
    await initialization
    await stores.search.getGardener({ externalId: match.params.id })

    // If gardener not found (invalid externalId, URL with accents, etc.) → noindex + show 404
    if (!stores.search.gardener || !stores.search.gardener.externalId) {
      addNoIndexIfNotExists()
      this.setState({ notFound: true, loading: false })
      return
    }

    // If the API returned a different externalId (redirect case), update URL
    if (stores.search.gardener.externalId !== match.params.id) {
      history.replace(`${PagesPaths.GARDENER.path}/${stores.search.gardener.externalId}`)
    }

    removeNoIndexIfExists()
    updateCanonicalLink(`${PagesPaths.GARDENER.path}/${stores.search.gardener.externalId}`)

    // We should have search on this page (from page or from cache)
    // if we don't it probably means that it's a fresh loading from a link
    // Set the correct values to display the correct informations (and breadcrumbs to redirect to /jardiniers)
    // That's the actual site behavior - although it's bugged and redirect to /jardiniers/null
    if (stores.search.isEmpty()) {
      stores.search.fromJardiniers = true
      stores.search.setEntryFunnel(LeadFunnels.SITE_CARTE)
      await stores.search.setQuery({ code1: this.code })
    }

    // Build enriched SEO metadata
    const gardener = stores.search.gardener
    const name = (gardener.name || '').trim()
    const city = this.city
    const postalCode = this.code
    const services = gardener.proposedServices || []
    const experience = gardener.since

    const metaTitle = buildGardenerMetaTitle(name, city, postalCode, services)
    const metaDescription = buildGardenerMetaDescription(name, city, postalCode, services, experience)

    stores.metadata.values = {
      logo: t('common:seo:jardinier:logo', { postalCode: postalCode, city: city }),
      alt: t('common:seo:jardinier:alt', { postalCode: postalCode, city: city }),
      title: metaTitle,
      description: metaDescription,
      keywords: t('common:seo:jardinier:keywords', { postalCode: postalCode, city: city })
    }

    // Inject JSON-LD structured data (LocalBusiness + BreadcrumbList)
    const jsonLd = buildGardenerJsonLd(gardener, city, postalCode, gardener.externalId)
    const breadcrumbJsonLd = buildGardenerBreadcrumbJsonLd(name, city, postalCode, gardener.externalId)
    if (jsonLd) {
      injectJsonLd([jsonLd, breadcrumbJsonLd])
    }

    // Open Graph + Twitter Card
    injectOpenGraph({
      title: metaTitle,
      description: metaDescription,
      url: 'https://www.cmonjardinier.com/app/jardinier/' + gardener.externalId,
      image: gardener.profilePicture ? gardener.profilePicture.path : undefined,
      type: 'profile'
    })

    this.setState({ loading: false })

    // Lazy-load reviews and enrich JSON-LD with AggregateRating
    if (gardener.entityRelationId) {
      getReviews({ entityRelationId: gardener.entityRelationId })
        .then(function (data) {
          if (data && data.ratings && data.ratings.count > 0 && jsonLd) {
            injectJsonLd([addRatingsToJsonLd(jsonLd, data.ratings), breadcrumbJsonLd])
          }
        })
        .catch(function () {
          // Reviews unavailable — JSON-LD stays without AggregateRating
        })
    }
  }

  componentWillUnmount () {
    removeCanonicalLink()
    removeNoIndexIfExists()
    removeJsonLd()
    removeOpenGraph()
  }

  get code () {
    // check if gardener has zipCodes, use the one that is in the search, or the first one in common with allzipcodesofcity
    const gardener = stores.search.gardener || {}
    const zipCodes = sanitizeZipCodes(gardener.zipCodes)
    if (zipCodes.length) {
      if (stores.search.search.postalCode && zipCodes.includes(stores.search.search.postalCode)) {
        return stores.search.search.postalCode
      }
      if (stores.search.search.allPostalCodesOfCity) {
        const common = zipCodes.filter((code) => stores.search.search.allPostalCodesOfCity.includes(code))
        if (common.length) {
          return common[0]
        }
      }
    }
    return zipCodes[0] || stores.search.search.postalCode
  }

  get city () {
    const gardener = stores.search.gardener || {}
    const city = gardener.city
    return stores.search.search.city || city
  }

  get breadcrumbs () {
    const gardener = stores.search.gardener || {}
    const name = (gardener.name || '').trim()
    const code = this.code
    if (stores.search.fromJardiniers) {
      if (stores.search.search.sponsor) {
        return [
          {
            name: this.props.t('common:jardiniers:gardeners_sponsor', { sponsor: stores.search.search.sponsor }),
            path: `${PagesPaths.GARDENERS.path}/${stores.search.search.sponsor}`
          },
          name ? { name: name } : undefined
        ]
      }
      return [
        stores.search.search.city &&
          {
            name: this.props.t('common:jardiniers:gardeners_at', { city: stores.search.search.city })
          },
        stores.search.search.postalCode &&
          {
            name: stores.search.search.postalCode,
            path: `${PagesPaths.GARDENERS.path}/${stores.search.search.city}/${code}`
          },
        name ? { name: name } : undefined
      ].filter((el) => !!el)
    } else {
      return [
        { name: `${this.props.t('common:visits:my_visit')}`, path: '/demande' },
        name ? { name: name } : undefined
      ].filter((el) => !!el)
    }
  }

  render () {
    if (this.state.loading) {
      return (
        <div className='layout__container --with-padding-bottom'>
          <div className='gardener-detail'>
            <div className='gardener-detail__skeleton'>
              <div className='gardener-detail__skeleton-header'>
                <div className='gardener-detail__skeleton-avatar' />
                <div className='gardener-detail__skeleton-info'>
                  <div className='gardener-detail__skeleton-line gardener-detail__skeleton-line--lg' />
                  <div className='gardener-detail__skeleton-line gardener-detail__skeleton-line--md' />
                  <div className='gardener-detail__skeleton-line gardener-detail__skeleton-line--sm' />
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
    if (this.state.notFound) {
      return (
        <div className='layout__container home__container--padding'>
          <div className='centered--container'>
            <h1>{this.props.t('common:404:title')}</h1>
            <p />
            <p><button style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#009d3d', fontFamily: 'inherit', fontSize: 'inherit', textDecoration: 'underline', padding: 0 }} onClick={() => this.props.history.goBack()}>{this.props.t('common:404:link')}</button></p>
          </div>
        </div>
      )
    }
    return (
      <div>
        <SubHero breadcrumb={this.breadcrumbs} />
        <div className='layout__container --with-padding-bottom'>
          <GardenerDetail
            gardener={stores.search.gardener}
            postalCode={this.code}
            city={this.city}
          />
        </div>
      </div>)
  }
}

GardenerPageComponent.propTypes = {
  t: func
}

export const GardenerPage = translate()(view(GardenerPageComponent))



// WEBPACK FOOTER //
// ./src/pages/jardiniers/GardenerPage.js